/**
 * TanStack Query hooks for the SubContractorHub connection flow.
 *
 * - useSubContractorHubIntegration: read-only integration row fetch (AC6).
 * - useTestSubContractorHubConnection: mutation wrapping POST /test-connection.
 * - useConnectSubContractorHub: mutation wrapping POST /connect; invalidates
 *   the integration query on success so AC6 reflects the new state.
 *
 * SECURITY: API keys are passed through the mutationFn to the service and
 * MUST NOT be persisted to query cache or any global state. Treat the key
 * as ephemeral component-local state only.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTenant } from '@/contexts/TenantContext';
import { subcontractorHubService } from '@/services/integrations/subcontractorHubService';

export function useSubContractorHubIntegration() {
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.tenant_id;
  return useQuery({
    queryKey: ['subcontractorhub', tenantId, 'integration'],
    queryFn: () => subcontractorHubService.getIntegration(tenantId as string),
    enabled: !!tenantId,
    staleTime: 30_000,
  });
}

export function useTestSubContractorHubConnection() {
  return useMutation({
    mutationFn: subcontractorHubService.testConnection,
  });
}

export function useConnectSubContractorHub() {
  const queryClient = useQueryClient();
  const { currentTenant } = useTenant();
  return useMutation({
    mutationFn: subcontractorHubService.connect,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['subcontractorhub', currentTenant?.tenant_id, 'integration'],
      });
    },
  });
}

/**
 * SCH-3.5: update the integration's fallback_owner_user_id. Pass NULL to
 * clear. Invalidates the integration query so the Connection tab refetches
 * the new value.
 */
export function useUpdateSubContractorHubFallbackOwner() {
  const queryClient = useQueryClient();
  const { currentTenant } = useTenant();
  return useMutation({
    mutationFn: ({
      integrationId,
      userId,
    }: {
      integrationId: string;
      userId: string | null;
    }) => subcontractorHubService.updateFallbackOwner(integrationId, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['subcontractorhub', currentTenant?.tenant_id, 'integration'],
      });
    },
  });
}

/**
 * SCH-5.5: resume a sync paused by the project-count-drop detector.
 * Server re-baselines last_run_project_count to today's SCH total and flips
 * last_sync_status back to 'active'. Invalidates the integration query so
 * the SyncStatusCard refetches the new state.
 */
export function useResumeAfterDrop() {
  const queryClient = useQueryClient();
  const { currentTenant } = useTenant();
  return useMutation({
    mutationFn: subcontractorHubService.resumeAfterDrop,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['subcontractorhub', currentTenant?.tenant_id, 'integration'],
      });
    },
  });
}

/**
 * SCH-5.7: resume a sync auto-paused after consecutive scheduled-sync
 * failures. Resets the failure counter, clears auto_paused_at, and flips
 * last_sync_status back to 'active'. Invalidates the integration query so the
 * SyncStatusCard refetches the new state.
 */
export function useResumeAfterFailures() {
  const queryClient = useQueryClient();
  const { currentTenant } = useTenant();
  return useMutation({
    mutationFn: subcontractorHubService.resumeAfterFailures,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['subcontractorhub', currentTenant?.tenant_id, 'integration'],
      });
    },
  });
}

/**
 * SCH-4.1: update scheduled-sync config (enable toggle + interval).
 * SCH-5.7: also carries the auto_pause_threshold.
 * The backend PATCH endpoint computes next_scheduled_sync_at on enable
 * transition so the FE doesn't need to coordinate timestamps.
 */
export function useUpdateScheduledSync() {
  const queryClient = useQueryClient();
  const { currentTenant } = useTenant();
  return useMutation({
    mutationFn: (input: {
      enabled?: boolean;
      polling_interval_minutes?: number;
      auto_pause_threshold?: number;
    }) => subcontractorHubService.updateScheduledSync(input),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['subcontractorhub', currentTenant?.tenant_id, 'integration'],
      });
    },
  });
}

export function useRotateSubContractorHubKey() {
  const queryClient = useQueryClient();
  const { currentTenant } = useTenant();
  return useMutation({
    mutationFn: subcontractorHubService.rotateKey,
    onSuccess: () => {
      // Invalidate the integration query so SyncStatusCard pulls the new
      // api_key_last_four (and external_org_name on org-confirmed change).
      queryClient.invalidateQueries({
        queryKey: ['subcontractorhub', currentTenant?.tenant_id, 'integration'],
      });
    },
  });
}

export function useDisableSubContractorHubIntegration() {
  const queryClient = useQueryClient();
  const { currentTenant } = useTenant();
  return useMutation({
    mutationFn: subcontractorHubService.disableIntegration,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['subcontractorhub', currentTenant?.tenant_id, 'integration'],
      });
    },
  });
}

export function useEnableSubContractorHubIntegration() {
  const queryClient = useQueryClient();
  const { currentTenant } = useTenant();
  return useMutation({
    mutationFn: subcontractorHubService.enableIntegration,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['subcontractorhub', currentTenant?.tenant_id, 'integration'],
      });
    },
  });
}

export function useDeleteSubContractorHubIntegration() {
  const queryClient = useQueryClient();
  const { currentTenant } = useTenant();
  return useMutation({
    mutationFn: subcontractorHubService.deleteIntegration,
    onSuccess: () => {
      // After delete, getIntegration() will return null and SyncStatusCard
      // will be replaced by the empty-state Connect CTA.
      queryClient.invalidateQueries({
        queryKey: ['subcontractorhub', currentTenant?.tenant_id, 'integration'],
      });
    },
  });
}

export function useReconnectSubContractorHubIntegration() {
  const queryClient = useQueryClient();
  const { currentTenant } = useTenant();
  return useMutation({
    mutationFn: subcontractorHubService.reconnectIntegration,
    onSuccess: () => {
      // SyncStatusCard re-renders with the new org name + cleared sync-state.
      queryClient.invalidateQueries({
        queryKey: ['subcontractorhub', currentTenant?.tenant_id, 'integration'],
      });
    },
  });
}
